import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Container, Row, Col, Form, Button, Card, Alert, Spinner, Image, Badge } from "react-bootstrap";
import "../../assets/css/InvoiceForm.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { adminInvoicesAPI, usersAPI } from "../../api/apiClient";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { FaPlus, FaTrashAlt } from "react-icons/fa";

const invoiceFieldNames = [
  "title",
  "firma_name",
  "kunde_name",
  "strasse_hausnummer",
  "postleitzahl_staat_ort",
  "land",
  "email",
  "ust_idnr",
  "rechnung",
  "unsrer_firma",
  "postleitzahl_staat_ort_2",
  "strasse_hausnummer_2",
  "land_2",
  "st_nr",
  "ust_idnr_2",
  "zaher_rawas",
  "telefonnummer",
  "email_2",
  "webseite",
  "rechnungsnummer",
  "kundennummer",
  "zahlungsziel",
  "ausstellungsdatum",
  "zahlungsform",
  "bedanken_uns",
  "betrag_2",
  "nettobetrag",
  "ust_19_von",
  "gesamtbetrag",
  "zu_bezahlen",
  "bankverbindung",
  "zahlungsempfanger",
  "bankname",
  "iban",
  "swift_bic",
];

const emptyItem = () => ({
  auftrag_nr: "",
  abholung: "",
  zustellung: "",
  menge: "",
  menge_weight: "",
  preis: "",
  rabatt_prozent: "",
  betrag: "",
});

const toDateInputValue = (date) => {
  if (!date) return "";

  if (typeof date === "string") {
    const value = date.trim();

    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }

    const germanDate = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (germanDate) {
      const [, day, month, year] = germanDate;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "";

  const localDate = new Date(parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

const getInvoiceDataFromResponse = (responseData) => responseData?.data || responseData || {};

const normalizeInvoiceData = (invoiceData) => {
  const normalizedData = {};

  invoiceFieldNames.forEach((fieldName) => {
    normalizedData[fieldName] = invoiceData?.[fieldName] ?? "";
  });

  return {
    ...normalizedData,
    logo: invoiceData?.logo || "",
    ausstellungsdatum: toDateInputValue(invoiceData?.ausstellungsdatum),
    zahlungsziel: toDateInputValue(invoiceData?.zahlungsziel),
  };
};

export default function InvoiceForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const fileRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [data, setData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [includeVat, setIncludeVat] = useState(true);
  const [vatRate, setVatRate] = useState(19);

  // تعريف الحقول المطلوبة
  const requiredFields = useMemo(
    () => ["title", "rechnungsnummer", "ausstellungsdatum", "zahlungsziel"],
    []
  );

  const [formData, setFormData] = useState({
    logo: "",
    title: "",
    firma_name: "",
    kunde_name: "",
    strasse_hausnummer: "",
    postleitzahl_staat_ort: "",
    land: "Deutschland",
    email: "",
    ust_idnr: "",
    rechnung: "RECHNUNG",
    unsrer_firma: "TransCiroX GmBH",
    postleitzahl_staat_ort_2: "51766 Engelskirchen",
    strasse_hausnummer_2: "Hauptstraße 10",
    land_2: "Deutschland",
    st_nr: "212/5711/3152",
    ust_idnr_2: "DE456589185",
    zaher_rawas: "TransCirox",
    telefonnummer: "+4917655439634",
    email_2: "Kontakt@transcirox.com",
    webseite: "www.transcirox.com",
    rechnungsnummer: "",
    kundennummer: "",
    zahlungsziel: "",
    ausstellungsdatum: new Date().toISOString().split("T")[0],
    zahlungsform: "Überweisung",
    bedanken_uns:
      "Wir bedanken uns für Ihren Auftrag und stellen folgende Positionen in Rechnung:",
    betrag_2: "",
    nettobetrag: "",
    ust_19_von: "",
    gesamtbetrag: "",
    zu_bezahlen: "",
    bankverbindung: "BANKVERBINDUNG:",
    zahlungsempfanger: " TransCirox GmbH",
    bankname: "Deutsche Bank",
    iban: "DE41384700910103360400",
    swift_bic: "DEUTDEDW384",
  });

  // ✅ حالة العناصر المتعددة
  const [items, setItems] = useState([emptyItem()]);

  // دالة التحقق من صحة الحقول
  const validateField = useCallback(
    (name, value) => {
      if (requiredFields.includes(name) && (!value || value.trim() === "")) {
        return t("field_required");
      }

      if (name === "email" && value && !/\S+@\S+\.\S+/.test(value)) {
        return t("invalid_email");
      }

      return null;
    },
    [requiredFields, t]
  );

  // دالة التحقق من جميع الحقول
  const validateForm = useCallback(() => {
    const errors = {};
    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) errors[field] = error;
    });
    return errors;
  }, [formData, requiredFields, validateField]);

  // تحديث التحقق عند تغيير الحقول
  useEffect(() => {
    const errors = validateForm();
    setValidationErrors(errors);
  }, [formData, validateForm]);

  // معالجة فقدان التركيز من حقل
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchCompanyInfo();
      await fetchUsers();
    };
    loadData();
  }, []);

  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true);
      setError("");
      const response = await usersAPI.index();
      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);
        setUsers(fetchedData);
      } else {
        setError(response.message || t("failed_to_fetch_users"));
      }
    } catch (err) {
      setError(t("failed_to_fetch_users"));
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await adminInvoicesAPI.getCompanyInfo();
      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);
        setCompanies(fetchedData);
      } else {
        setError(response.message || t("failed_to_fetch_users"));
      }
    } catch (error) {
      setError(t("failed_to_fetch_users"));
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (e) => {
    const value = e.target.value;

    if (!value) {
      setSelectedUser(null);
      setFormData((prev) => ({
        ...prev,
        firma_name: "",
        kunde_name: "",
        strasse_hausnummer: "",
        postleitzahl_staat_ort: "",
        land: "Deutschland",
        email: "",
        ust_idnr: "",
      }));
      return;
    }

    const [type, id] = value.split("-");

    if (type === "company") {
      const company = companies.find((c) => c.id.toString() === id);
      if (company) {
        setSelectedUser({ ...company, type: "company" });
        setFormData((prev) => ({
          ...prev,
          firma_name: company.firma_name || company.company_name || "",
          kunde_name: company.kunde_name || "",
          strasse_hausnummer: company.strasse_hausnummer || company.address || "",
          postleitzahl_staat_ort:
            company.postleitzahl_staat_ort || company.postal_code || "",
          land: company.land || "Deutschland",
          email: company.email || "",
          ust_idnr: company.ust_idnr || company.tax_number || "",
        }));
      }
    } else if (type === "user") {
      const user = users.find((u) => u.id.toString() === id);
      if (user) {
        setSelectedUser({ ...user, type: "user" });
        setFormData((prev) => ({
          ...prev,
          firma_name: user.company_name || user.firstname || "",
          kunde_name: user.lastname || user.nicname || "",
          strasse_hausnummer: user.street_address || user.address || "",
          postleitzahl_staat_ort: user.postal_code || user.zip_code || "",
          land: user.country || user.addressone || "Deutschland",
          email: user.email || "",
          ust_idnr: user.tax_number || user.vat_id || "",
        }));
      }
    }
  };

  // Fetch invoice data for edit mode
  useEffect(() => {
    if (id) {
      const fetchInvoice = async () => {
        try {
          setLoading(true);
          setError("");
          const response = await adminInvoicesAPI.show(id);

          if (response.success) {
            const invoiceData = getInvoiceDataFromResponse(response.data);
            setFormData((prev) => ({ ...prev, ...normalizeInvoiceData(invoiceData) }));

            // ✅ حمّل الـ items
            if (Array.isArray(invoiceData.items) && invoiceData.items.length > 0) {
              setItems(
                invoiceData.items.map((it) => ({
                  auftrag_nr: it.auftrag_nr || "",
                  abholung: it.abholung || "",
                  zustellung: it.zustellung || "",
                  menge: it.menge || "",
                  menge_weight: it.menge_weight || "",
                  preis: it.preis || "",
                  rabatt_prozent: it.rabatt_prozent || "",
                  betrag: it.betrag || "",
                }))
              );
            }
            setRemoveLogo(false);
          } else {
            setError(response.message || t("failed_to_fetch_data"));
            Swal.fire({
              icon: "error",
              title: t("error"),
              text: response.message || t("failed_to_fetch_data"),
              timer: 2000,
              showConfirmButton: false,
            });
          }
        } catch (err) {
          setError(t("failed_to_fetch_data"));
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("failed_to_fetch_data"),
            timer: 2000,
            showConfirmButton: false,
          });
          console.error("Error fetching invoice:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchInvoice();
    } else if (location.state?.invoiceData) {
      const invoiceData = getInvoiceDataFromResponse(location.state.invoiceData);
      setFormData((prev) => ({
        ...prev,
        ...normalizeInvoiceData(invoiceData),
      }));
      setRemoveLogo(false);
    }
  }, [id, location.state, t]);

  // ✅ حساب الإجماليات من مجموع الـ items
  useEffect(() => {
    const totalNet = items.reduce((sum, it) => {
      const price = parseFloat(it.preis) || 0;
      const discount = parseFloat(it.rabatt_prozent) || 0;
      return sum + price * (1 - discount / 100);
    }, 0);

    const vat = includeVat ? totalNet * (vatRate / 100) : 0;
    const total = totalNet + vat;

    const fmt = (n) => n.toFixed(2).replace(".", ",");

    setFormData((prev) => ({
      ...prev,
      nettobetrag: fmt(totalNet),
      betrag_2: fmt(vat),
      ust_19_von: fmt(totalNet),
      gesamtbetrag: fmt(total),
      zu_bezahlen: fmt(total),
    }));
  }, [items, includeVat, vatRate]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      setLogoFile(file);
      setRemoveLogo(false);
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData((prev) => ({ ...prev, logo: reader.result }));
        };
        reader.readAsDataURL(file);
      } else {
        setFormData((prev) => ({ ...prev, logo: "" }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Reset file input
  const resetFileInput = () => {
    setLogoFile(null);
    setRemoveLogo(true);
    setFormData((prev) => ({ ...prev, logo: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const addItem = () => {
    setItems((prev) => [...prev, emptyItem()]);
  };

  const removeItem = (index) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };

      // احسب betrag أوتوماتيك
      if (field === "preis" || field === "rabatt_prozent") {
        const price = parseFloat(next[index].preis) || 0;
        const discount = parseFloat(next[index].rabatt_prozent) || 0;
        const net = price * (1 - discount / 100);
        next[index].betrag = net.toFixed(2);
      }

      return next;
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // علام جميع الحقول كـ touched
    const allTouched = {};
    requiredFields.forEach((field) => {
      allTouched[field] = true;
    });
    setTouchedFields(allTouched);

    // التحقق من صحة النموذج
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const errorMessage = t("please_fill_all_required_fields");
      setError(errorMessage);

      Swal.fire({
        icon: "error",
        title: t("error"),
        text: errorMessage,
        timer: 2000,
        showConfirmButton: false,
      });

      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }

      return;
    }

    try {
      setSaving(true);
      setError("");

      const formDataToSend = new FormData();

      // الحقول العادية
      invoiceFieldNames.forEach((key) => {
        formDataToSend.append(key, formData[key] ?? "");
      });

      // ✅ Items — array notation بدل JSON.stringify
      items.forEach((item, idx) => {
        formDataToSend.append(`items[${idx}][auftrag_nr]`, item.auftrag_nr ?? "");
        formDataToSend.append(`items[${idx}][abholung]`, item.abholung ?? "");
        formDataToSend.append(`items[${idx}][zustellung]`, item.zustellung ?? "");
        formDataToSend.append(`items[${idx}][menge]`, item.menge ?? "");
        formDataToSend.append(`items[${idx}][menge_weight]`, item.menge_weight ?? "");
        formDataToSend.append(`items[${idx}][preis]`, item.preis ?? "");
        formDataToSend.append(`items[${idx}][rabatt_prozent]`, item.rabatt_prozent ?? "");
        formDataToSend.append(`items[${idx}][betrag]`, item.betrag ?? "");
      });

      if (logoFile) {
        formDataToSend.append("logo", logoFile);
      } else if (removeLogo) {
        formDataToSend.append("remove_logo", "1");
      }

      let response;
      if (id) {
        response = await adminInvoicesAPI.update(id, formDataToSend);
      } else {
        response = await adminInvoicesAPI.store(formDataToSend);
      }

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: id
            ? t("invoice_updated_successfully")
            : t("invoice_created_successfully"),
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          navigate("/admin/invoices", {
            state: {
              fromPage: location.state?.fromPage || 1,
              highlightId: id || null,
            },
          });
        });
      } else {
        setError(response.message || t("operation_failed"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("operation_failed"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("operation_failed"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err?.response?.data?.message || t("operation_failed"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error submitting form:", err);
    } finally {
      setSaving(false);
    }
  };

  // دالة مساعدة لتحديد كلاس الحقل
  const getFieldClassName = (fieldName) => {
    const isInvalid = touchedFields[fieldName] && validationErrors[fieldName];
    return `form-control ${isInvalid ? "is-invalid" : ""}`;
  };

  // دالة لعرض رسالة الخطأ أسفل الحقل
  const renderFieldError = (fieldName) => {
    if (touchedFields[fieldName] && validationErrors[fieldName]) {
      return (
        <Form.Control.Feedback type="invalid">
          {validationErrors[fieldName]}
        </Form.Control.Feedback>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" />
        <p className="mt-2">{t("loading")}</p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <Card className="shadow border-0 rounded-4 p-4 custom-invoice-card bg-dashboard">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="text-sidebar fw-bold mb-0 h3">
              <span role="img" aria-label="invoice">📧</span>{" "}
              {id ? t("Edit Invoice") : t("Add Invoice")}
            </h1>
            <Button
              variant="secondary"
              onClick={() =>
                navigate("/admin/invoices", {
                  state: {
                    fromPage: location.state?.fromPage || 1,
                    highlightId: id || null,
                  },
                })
              }
              disabled={saving}
              aria-label={t("back_to_list")}
            >
              {t("Back to List")}
            </Button>
          </div>

          {error && (
            <Alert variant="danger" role="alert" className="mb-4">
              <Alert.Heading>{t("Error")}</Alert.Heading>
              <p>{error}</p>
            </Alert>
          )}

          <Form onSubmit={handleSubmit} noValidate>
            <section aria-labelledby="basic-info-heading">
              <h2 id="basic-info-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("basic_information")}
              </h2>

              {(formData.logo || logoFile) && (
                <Form.Group className="mb-3">
                  <Form.Label>{t("Current Logo")}</Form.Label>
                  <div>
                    <Image
                      src={formData.logo}
                      alt={t("invoice_logo")}
                      width={100}
                      height={100}
                      rounded
                      style={{ objectFit: "cover" }}
                      loading="lazy"
                    />
                    <br />
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={resetFileInput}
                      disabled={saving}
                      className="mt-2"
                      aria-label={t("remove_logo")}
                      type="button"
                    >
                      {t("remove_logo")}
                    </Button>
                  </div>
                </Form.Group>
              )}

              <Row className="mb-4">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("Title")} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName("title")}
                      required
                    />
                    {renderFieldError("title")}
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("Logo")}</Form.Label>
                    <Form.Control
                      type="file"
                      name="logo"
                      ref={fileRef}
                      onChange={handleInputChange}
                      accept="image/*"
                      aria-describedby="logoHelp"
                    />
                    <Form.Text id="logoHelp" muted>
                      {t("Upload company logo")}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <hr />

            <section aria-labelledby="company-info-heading">
              <h2 id="company-info-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("company_information")}
              </h2>

              <Row className="mb-4">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("select_user")}</Form.Label>
                    <div className="custom-select-dropdown">
                      <Form.Select
                        name="user_id"
                        onChange={handleUserSelect}
                        value={selectedUser?.id ? `${selectedUser.type}-${selectedUser.id}` : ""}
                        disabled={loading || (users.length === 0 && companies.length === 0)}
                        style={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: "1px solid #ced4da",
                          backgroundColor: "#fff",
                        }}
                      >
                        <option value="">{t("select_user")}</option>

                        {companies.length > 0 && (
                          <optgroup label={t("companies")}>
                            {companies.map((company) => (
                              <option key={`company-${company.id}`} value={`company-${company.id}`}>
                                🏢 {company.firma_name || company.company_name || company.kunde_name}
                                {company.email && ` (${company.email})`}
                              </option>
                            ))}
                          </optgroup>
                        )}

                        {users.length > 0 && (
                          <optgroup label={t("users")}>
                            {users.map((user) => (
                              <option key={`user-${user.id}`} value={`user-${user.id}`}>
                                👤 {user.firstname && user.lastname
                                  ? `${user.firstname} ${user.lastname}`
                                  : user.nicname || user.email}
                                {user.email && ` (${user.email})`}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </Form.Select>
                    </div>
                    <Form.Text muted>{t("select_user_to_form")}</Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {selectedUser && (
                <div className="mb-3 p-2 bg-light rounded">
                  <small className="text-muted">
                    {selectedUser.type === "company" ? "🏢 " : "👤 "}
                    {t("selected")}: <strong>{selectedUser.firma_name || selectedUser.firstname}</strong>
                    <span className="ms-2 badge bg-secondary">
                      {selectedUser.type === "company" ? t("company") : t("user")}
                    </span>
                  </small>
                </div>
              )}

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("firma_name")}</Form.Label>
                    <Form.Control
                      name="firma_name"
                      value={formData.firma_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName("firma_name")}
                    />
                    {renderFieldError("firma_name")}
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("kunde_name")}</Form.Label>
                    <Form.Control
                      name="kunde_name"
                      value={formData.kunde_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName("kunde_name")}
                    />
                    {renderFieldError("kunde_name")}
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("strasse_hausnummer")}</Form.Label>
                    <Form.Control
                      name="strasse_hausnummer"
                      value={formData.strasse_hausnummer}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName("strasse_hausnummer")}
                    />
                    {renderFieldError("strasse_hausnummer")}
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("postleitzahl_staat_ort")}</Form.Label>
                    <Form.Control
                      name="postleitzahl_staat_ort"
                      value={formData.postleitzahl_staat_ort}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName("postleitzahl_staat_ort")}
                    />
                    {renderFieldError("postleitzahl_staat_ort")}
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("land")}</Form.Label>
                    <Form.Control
                      name="land"
                      value={formData.land}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("Email")}</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName("email")}
                    />
                    {renderFieldError("email")}
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("ust_idnr")}</Form.Label>
                    <Form.Control
                      name="ust_idnr"
                      value={formData.ust_idnr}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <hr />

            <section aria-labelledby="invoice-details-heading">
              <h2 id="invoice-details-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("invoice_details1")}
              </h2>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("Invoice")}</Form.Label>
                    <Form.Control
                      name="rechnung"
                      value={formData.rechnung}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("unsrer_firma")}</Form.Label>
                    <Form.Control
                      name="unsrer_firma"
                      value={formData.unsrer_firma}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("postleitzahl_staat_ort_2")}</Form.Label>
                    <Form.Control
                      name="postleitzahl_staat_ort_2"
                      value={formData.postleitzahl_staat_ort_2}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("strasse_hausnummer_2")}</Form.Label>
                    <Form.Control
                      name="strasse_hausnummer_2"
                      value={formData.strasse_hausnummer_2}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("land_2")}</Form.Label>
                    <Form.Control
                      name="land_2"
                      value={formData.land_2}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("st_nr")}</Form.Label>
                    <Form.Control
                      name="st_nr"
                      value={formData.st_nr}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("ust_idnr_2")}</Form.Label>
                    <Form.Control
                      name="ust_idnr_2"
                      value={formData.ust_idnr_2}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("zaher_rawas")}</Form.Label>
                    <Form.Control
                      name="zaher_rawas"
                      value={formData.zaher_rawas}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("telefonnummer")}</Form.Label>
                    <Form.Control
                      name="telefonnummer"
                      value={formData.telefonnummer}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("email_2")}</Form.Label>
                    <Form.Control
                      type="email"
                      name="email_2"
                      value={formData.email_2}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("webseite")}</Form.Label>
                    <Form.Control
                      name="webseite"
                      value={formData.webseite}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <hr />

            <section aria-labelledby="invoice-date-heading">
              <h2 id="invoice-date-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("invoice_date")}
              </h2>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("rechnungsnummer")} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="rechnungsnummer"
                      value={formData.rechnungsnummer}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName("rechnungsnummer")}
                    />
                    {renderFieldError("rechnungsnummer")}
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("kundennummer")}</Form.Label>
                    <Form.Control
                      name="kundennummer"
                      value={formData.kundennummer}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("ausstellungsdatum")} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="ausstellungsdatum"
                      value={formData.ausstellungsdatum}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName("ausstellungsdatum")}
                    />
                    {renderFieldError("ausstellungsdatum")}
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("zahlungsziel")} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="zahlungsziel"
                      value={formData.zahlungsziel}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName("zahlungsziel")}
                    />
                    {renderFieldError("zahlungsziel")}
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("zahlungsform")}</Form.Label>
                    <Form.Control
                      name="zahlungsform"
                      value={formData.zahlungsform}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <hr />

            <Row className="mb-4">
              <Col xs={12} md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">{t("bedanken_uns")}</Form.Label>
                  <Form.Control
                    name="bedanken_uns"
                    value={formData.bedanken_uns}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr />

            <section aria-labelledby="items-heading">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 id="items-heading" className="h5 fw-bold text-sidebar mb-0">
                  {t("Items")}
                </h2>
                <Button variant="success" size="sm" onClick={addItem} type="button">
                  <FaPlus className="me-1" /> {t("Add")}
                </Button>
              </div>

              {items.map((item, index) => (
                <Card key={index} className="mb-3 border">
                  <Card.Body>
                    <div className="d-flex justify-content-between mb-2">
                      <strong>#{index + 1}</strong>
                      {items.length > 1 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeItem(index)}
                          type="button"
                        >
                          <FaTrashAlt />
                        </Button>
                      )}
                    </div>

                    <Row className="g-2">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>{t("auftrag_nr")}</Form.Label>
                          <Form.Control
                            value={item.auftrag_nr}
                            onChange={(e) => updateItem(index, "auftrag_nr", e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>{t("abholung")}</Form.Label>
                          <Form.Control
                            value={item.abholung}
                            onChange={(e) => updateItem(index, "abholung", e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>{t("zustellung")}</Form.Label>
                          <Form.Control
                            value={item.zustellung}
                            onChange={(e) => updateItem(index, "zustellung", e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={1}>
                        <Form.Group>
                          <Form.Label>{t("menge")}</Form.Label>
                          <Form.Control
                            value={item.menge}
                            onChange={(e) => updateItem(index, "menge", e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>{t("menge_weight")}</Form.Label>
                          <Form.Control
                            type="number"
                            value={item.menge_weight}
                            onChange={(e) => updateItem(index, "menge_weight", e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="g-2 mt-2">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>{t("price")}</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            value={item.preis}
                            onChange={(e) => updateItem(index, "preis", e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>{t("rabatt_prozent")}</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            value={item.rabatt_prozent}
                            onChange={(e) => updateItem(index, "rabatt_prozent", e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>{t("betrag")}</Form.Label>
                          <Form.Control value={item.betrag} readOnly />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </section>

            <hr />

            <section aria-labelledby="totals-heading">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 id="totals-heading" className="h5 fw-bold text-sidebar mb-0">
                  {t("totals")}
                </h2>

                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0 text-muted small">
                      {t("vat_included")}
                    </Form.Label>
                    <Form.Check
                      type="switch"
                      id="vat-toggle"
                      checked={includeVat}
                      onChange={(e) => setIncludeVat(e.target.checked)}
                      className="vat-toggle-switch"
                    />
                  </div>

                  <div className="d-flex align-items-center gap-1" style={{ maxWidth: "80px" }}>
                    <Form.Control
                      type="number"
                      size="sm"
                      value={vatRate}
                      onChange={(e) => setVatRate(parseFloat(e.target.value) || 0)}
                      disabled={!includeVat}
                      min="0"
                      max="100"
                      step="0.5"
                      style={{
                        width: "60px",
                        textAlign: "center",
                        background: includeVat ? "#fff" : "#e9ecef",
                      }}
                    />
                    <span className="text-muted small">%</span>
                  </div>

                  <Badge bg={includeVat ? "success" : "secondary"} className="ms-1">
                    {includeVat ? `${vatRate}%` : "0%"}
                  </Badge>
                </div>
              </div>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("betrag_2")}</Form.Label>
                    <Form.Control
                      name="betrag_2"
                      value={formData.betrag_2}
                      readOnly={includeVat}
                      onChange={(e) => {
                        if (!includeVat) {
                          setFormData((prev) => ({ ...prev, betrag_2: e.target.value }));
                        }
                      }}
                      style={!includeVat ? { background: "#fff", color: "#000" } : {}}
                    />
                    {!includeVat && <Form.Text muted>{t("manual_edit_enabled")}</Form.Text>}
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("nettobetrag")}</Form.Label>
                    <Form.Control
                      name="nettobetrag"
                      value={formData.nettobetrag}
                      readOnly={includeVat}
                      onChange={(e) => {
                        if (!includeVat) {
                          setFormData((prev) => ({ ...prev, nettobetrag: e.target.value }));
                        }
                      }}
                      style={!includeVat ? { background: "#fff", color: "#000" } : {}}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {includeVat
                        ? t("ust_von_with_rate", { rate: vatRate })
                        : t("ust_von_without_rate")}
                    </Form.Label>
                    <Form.Control
                      name="ust_19_von"
                      value={formData.ust_19_von}
                      readOnly={includeVat}
                      onChange={(e) => {
                        if (!includeVat) {
                          setFormData((prev) => ({ ...prev, ust_19_von: e.target.value }));
                        }
                      }}
                      style={!includeVat ? { background: "#fff", color: "#000" } : {}}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("gesamtbetrag")}</Form.Label>
                    <Form.Control
                      name="gesamtbetrag"
                      value={formData.gesamtbetrag}
                      readOnly={includeVat}
                      onChange={(e) => {
                        if (!includeVat) {
                          setFormData((prev) => ({ ...prev, gesamtbetrag: e.target.value }));
                        }
                      }}
                      style={!includeVat ? { background: "#fff", color: "#000" } : {}}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("zu_bezahlen")}</Form.Label>
                    <Form.Control
                      name="zu_bezahlen"
                      value={formData.zu_bezahlen}
                      readOnly={includeVat}
                      onChange={(e) => {
                        if (!includeVat) {
                          setFormData((prev) => ({ ...prev, zu_bezahlen: e.target.value }));
                        }
                      }}
                      style={!includeVat ? { background: "#fff", color: "#000" } : {}}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <hr />

            <section aria-labelledby="bank-details-heading">
              <h2 id="bank-details-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("bank_details")}
              </h2>

              <Row className="mb-3">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("bankverbindung")}</Form.Label>
                    <Form.Control
                      name="bankverbindung"
                      value={formData.bankverbindung}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("zahlungsempfanger")}</Form.Label>
                    <Form.Control
                      name="zahlungsempfanger"
                      value={formData.zahlungsempfanger}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("bankname")}</Form.Label>
                    <Form.Control
                      name="bankname"
                      value={formData.bankname}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-5">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("iban")}</Form.Label>
                    <Form.Control
                      name="iban"
                      value={formData.iban}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("swift_bic")}</Form.Label>
                    <Form.Control
                      name="swift_bic"
                      value={formData.swift_bic}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <div className="text-end" role="group" aria-label={t("Form actions")}>
              <Button
                variant="secondary"
                className="me-2"
                onClick={() =>
                  navigate("/admin/invoices", {
                    state: {
                      fromPage: location.state?.fromPage || 1,
                      highlightId: id || null,
                    },
                  })
                }
                disabled={saving}
                type="button"
              >
                {t("Cancel")}
              </Button>
              <Button
                variant="primary"
                className="px-4 rounded-3 shadow"
                type="submit"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t("Saving...")}
                  </>
                ) : (
                  t("save")
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}